import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Clock, LogIn, LogOut, Wallet } from 'lucide-react';
import type { AttendanceRecord } from '@/types';
import {
  ATTENDANCE_STATUS,
  formatAttendanceDate,
  formatMinutes,
  formatNaira,
  formatTime,
  getLateStatusColor,
} from '@/utils';
import { cn } from '@/utils/cn';

interface AttendanceCardProps {
  attendance: AttendanceRecord;
  showDetails?: boolean;
}

export function AttendanceCard({ attendance, showDetails = true }: AttendanceCardProps) {
  const statusColor = getLateStatusColor(attendance.lateCategory);
  const isLate = attendance.minutesLate && attendance.minutesLate > 0;
  const isMissedClockIn = attendance.status === ATTENDANCE_STATUS.MISSED_CLOCK_IN;
  const isMissedClockOut = attendance.status === ATTENDANCE_STATUS.MISSED_CLOCK_OUT;
  const totalDeductions = attendance.deductions
    .filter((d) => !d.deletedAt)
    .reduce((sum, d) => sum + d.amount, 0);
  const pardonedDeduction = attendance.deductions.some((d) => d.pardoned);

  const clockInTime = attendance.clockIn ? formatTime(attendance.clockIn.timestamp) : null;
  const clockOutTime = attendance.clockOut ? formatTime(attendance.clockOut.timestamp) : null;

  const { dayName, dayNum, monthName } = formatAttendanceDate(attendance.date);

  return (
    <motion.div
      whileHover={{ scale: 1.002 }}
      className={cn(
        'group relative overflow-hidden transition-all duration-300',
        showDetails
          ? 'p-4 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm hover:shadow-md mb-2'
          : 'p-2.5 rounded-lg hover:bg-neolab-green/5 dark:hover:bg-white/5',
      )}
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center justify-center w-12 h-12 rounded-lg bg-neolab-green/10 dark:bg-white/10 border border-neolab-green/20 dark:border-white/10 group-hover:border-neolab-green/30 transition-colors">
            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-tighter">
              {monthName}
            </span>
            <span className="text-xl font-black text-slate-900 dark:text-white leading-none">
              {dayNum}
            </span>
            <span className="text-[10px] font-bold text-neolab-green uppercase">
              {dayName.slice(0, 3)}
            </span>
          </div>

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/10">
                <LogIn className="w-4 h-4" />
                <span className="text-sm font-black">{clockInTime || '--:--'}</span>
              </div>
              <div className="w-3 h-px bg-neolab-green/20 dark:bg-white/10" />
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/10">
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-black">{clockOutTime || '--:--'}</span>
              </div>
            </div>

            {isLate ? (
              <div className="flex items-center gap-1 text-rose-500 font-bold text-[11px] uppercase tracking-wider">
                <Clock className="w-4 h-4" />
                <span>Late by {formatMinutes(attendance.minutesLate)}</span>
              </div>
            ) : isMissedClockIn ? (
              <div className="flex items-center gap-1 text-rose-500 font-bold text-[11px] uppercase tracking-wider">
                <Clock className="w-4 h-4" />
                <span>Missed Clock In</span>
              </div>
            ) : isMissedClockOut ? (
              <div className="flex items-center gap-1 text-rose-500 font-bold text-[11px] uppercase tracking-wider">
                <Clock className="w-4 h-4" />
                <span>Missed Clock Out</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-emerald-500 font-bold text-[11px] uppercase tracking-wider">
                <CheckCircle2 className="w-4 h-4" />
                <span>Punctual Arrival</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-4">
          {totalDeductions > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-500/10 border border-rose-500/10 text-rose-500">
              <Wallet className="w-4.5 h-4.5" />
              <span className="text-sm font-black">-{formatNaira(totalDeductions)}</span>
            </div>
          )}

          <div
            className={cn(
              'px-3 py-1.5 rounded-xl text-[11px] font-black uppercase tracking-widest border shadow-sm',
              statusColor === 'green' && 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
              statusColor === 'orange' && 'bg-amber-500/10 text-amber-600 border-amber-500/20',
              statusColor === 'red' && 'bg-rose-500/10 text-rose-600 border-rose-500/20',
            )}
          >
            {attendance.lateCategory === 'on_time'
              ? 'On Time'
              : attendance.lateCategory?.replace(/_/g, ' ')}
          </div>
          {pardonedDeduction && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-500/10 border border-green-500/10 text-green-500">
              <CheckCircle2 className="w-4.5 h-4.5" />
              <span className="text-sm font-black">Pardoned</span>
            </div>
          )}
        </div>
      </div>

      {showDetails && attendance.deductions && attendance.deductions.length > 0 && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="mt-4 pt-4 border-t border-neolab-green/5 dark:border-white/5"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-slate-400" />
            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
              Deduction Breakdown
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {attendance.deductions
              .filter((d) => !d.deletedAt)
              .map((deduction, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2.5 rounded-xl bg-neolab-green/5 dark:bg-white/5 border border-neolab-green/10 dark:border-white/10"
                >
                  <span className="text-sm font-bold text-slate-600 dark:text-slate-400">
                    {deduction.type === 'late_arrival'
                      ? formatMinutes(attendance.minutesLate)
                      : deduction.reason}
                  </span>
                  <span className="text-sm font-black text-rose-500">
                    {formatNaira(deduction.amount)}
                  </span>
                </div>
              ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default AttendanceCard;
